import { inject, Injectable, Signal, signal } from '@angular/core';
import {
    SetRoomsEvent,
    ApiRoom,
    Level,
    ApiSlab,
    ApiBuilding,
    ApiProject,
    ApiHeatLoadCalculation,
    CalculationSummary,
    CalculationBase
} from '../../models';
import { CRYPTO } from '../../tokens';
import { RoomByRoomApiService } from '../room-by-room-api.service';
import {
    mapApiBuildingToCalculationSummary,
    mapApiBuildingToLevelsView,
    mapHeatLoadCalculationsToApiModel,
    mapLevelsToApiModel,
    mapRoomsToApiModel,
    mapSlabsToApiModel
} from './building.mappers';
import {
    createLowerLevel,
    createUpperLevel,
    deleteLevel,
    deleteRoom,
    setRoomsToLevel,
    toggleHeatingAndCooling,
    updateCalculationSummary
} from './building.domain';
import { HeatLoadCalculationMethod } from '../../../../lib';
import { DefaultValues } from '../../models/default-values.interface';

const DEFAULT_ROOM_HEIGHT = 2.5;

@Injectable({
    providedIn: 'root'
})
export class BuildingService {
    private _project = signal<ApiProject | null>(null);
    private _levelsSignal = signal<Level[]>([]);
    private _calculationSummarySignal = signal<CalculationSummary | undefined>(undefined);
    private _calculationBaseSignal = signal<CalculationBase>({ totalLivingArea: 0, includeTotalBuilding: false });
    private _defaultValuesSignal = signal<DefaultValues>({ levelHeight: DEFAULT_ROOM_HEIGHT });

    private _crypto = inject(CRYPTO);
    private _innerLoadingSignal = signal<boolean>(false);
    private _innerErrorSignal = signal<boolean>(false);
    private _loadingSignal = signal<boolean>(false);
    private _errorSignal = signal<boolean>(false);
    private _activeProjectId: string | null = null;
    private _apiRoomsSnapshot: { [key: string]: ApiRoom } = {};
    private _apiProjectSnapshot: ApiProject | null = null;
    private _apiHeatLoadCalculations: ApiHeatLoadCalculation[] = [];
    private _apiSlabsSnapshot: { [key: string]: ApiSlab } = {};
    private _roomByRoomApiService = inject(RoomByRoomApiService);

    public getProject(): Signal<ApiProject | null> {
        return this._project.asReadonly();
    }

    public getLevels(): Signal<Level[]> {
        return this._levelsSignal.asReadonly();
    }

    public getInnerLoading(): Signal<boolean> {
        return this._innerLoadingSignal.asReadonly();
    }

    public getInnerError(): Signal<boolean> {
        return this._innerErrorSignal.asReadonly();
    }

    public getCalculationSummary(): Signal<CalculationSummary | undefined> {
        return this._calculationSummarySignal.asReadonly();
    }

    public getCalculationBase(): Signal<CalculationBase> {
        return this._calculationBaseSignal.asReadonly();
    }

    public getDefaultValues(): Signal<DefaultValues> {
        return this._defaultValuesSignal.asReadonly();
    }

    public getLoading(): Signal<boolean> {
        return this._loadingSignal.asReadonly();
    }

    public getError(): Signal<boolean> {
        return this._errorSignal.asReadonly();
    }

    public loadProject(projectId: string): void {
        this._activeProjectId = projectId;
        this._loadingSignal.set(true);
        this._errorSignal.set(false);

        this._roomByRoomApiService.getProject$(projectId).subscribe({
            next: project => {
                // store entire API rooms and slabs models (including properties that are not part of the UI model) to prevent losing data when patching
                // for example room.wallIds or room.tags are not mapped and we don't want to lose them as patching updates entire dictionaries
                this._apiProjectSnapshot = { ...project };
                this._project.set({ ...project });
                this._apiRoomsSnapshot = { ...project.building?.rooms };
                this._apiSlabsSnapshot = { ...project.building?.slabs };
                this._apiHeatLoadCalculations = [...(project.building?.heatLoadCalculations ?? [])];

                this._levelsSignal.set(mapApiBuildingToLevelsView(project.building));
                this._calculationSummarySignal.set(mapApiBuildingToCalculationSummary(project.building));
                this._calculationBaseSignal.set(this.extractCalculationBase(project.building));
                this._defaultValuesSignal.set(this.extractDefaultValues(project.building));

                this._loadingSignal.set(false);
                this._errorSignal.set(false);
            },
            error: err => {
                console.error('Failed to load project levels', err);
                this._loadingSignal.set(false);
                this._errorSignal.set(true);
                this._levelsSignal.set([]);
                this._calculationSummarySignal.set(undefined);
                this._calculationBaseSignal.set({ totalLivingArea: 0, includeTotalBuilding: false });
                this._defaultValuesSignal.set({
                    levelHeight: DEFAULT_ROOM_HEIGHT,
                    typeOfWindow: undefined,
                    typeOfSunProtection: undefined
                });
            }
        });
    }

    public updateDefaultValues(defaultValues: DefaultValues): void {
        if (!this._activeProjectId) return;

        this._innerLoadingSignal.set(true);

        this._roomByRoomApiService
            .patchProject$(this._activeProjectId, {
                building: {
                    buildingLevelHeight: defaultValues.levelHeight,
                    standardTypeOfWindow: defaultValues.typeOfWindow,
                    standardTypeOfSunProtection: defaultValues.typeOfSunProtection
                } as ApiBuilding
            })
            .subscribe({
                next: () => {
                    this._defaultValuesSignal.set(defaultValues);
                    this._innerLoadingSignal.set(false);
                },
                error: err => {
                    console.error('Failed to update default values', err);
                    this._innerLoadingSignal.set(false);
                }
            });
    }

    public updateCalculationBase(calculationBase: CalculationBase): void {
        if (!this._activeProjectId) return;

        this.patchBuildingToApi(this._levelsSignal(), { calculationBase });
    }

    public addUpperLevel(): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const updatedLevels = createUpperLevel(levels, this._crypto.randomUUID());

        this.patchBuildingToApi(updatedLevels);
    }

    public addLowerLevel(): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const updatedLevels = createLowerLevel(levels, this._crypto.randomUUID());

        this.patchBuildingToApi(updatedLevels);
    }

    public setRoomsToLevel(event: SetRoomsEvent): void {
        if (!this._activeProjectId) return;

        const defaultRoomHeight = this._defaultValuesSignal().levelHeight ?? DEFAULT_ROOM_HEIGHT;

        const levels = this._levelsSignal();
        const updatedLevels = setRoomsToLevel(levels, event, () => this._crypto.randomUUID(), defaultRoomHeight);
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public deleteRoom(roomId: string): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const updatedLevels = deleteRoom(levels, roomId);
        if (!updatedLevels) return;
        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public updateProject(project: ApiProject | null): void {
        if (!this._activeProjectId || !project?.building) return;

        this._innerLoadingSignal.set(true);
        this._innerErrorSignal.set(false);

        this._roomByRoomApiService
            .patchProject$(this._activeProjectId, {
                building: {
                    rooms: project.building.rooms,
                    slabs: project.building.slabs,
                    levels: project.building.levels,
                    walls: project.building.walls,
                    windows: project.building.windows
                }
            })
            .subscribe({
                next: () => {
                    this._innerLoadingSignal.set(false);
                    this._innerErrorSignal.set(false);
                    this.loadProject(this._activeProjectId!);
                },
                error: _error => {
                    this._innerLoadingSignal.set(false);
                    this._innerErrorSignal.set(true);
                }
            });
    }

    public deleteLevel(levelId: string): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const updatedLevels = deleteLevel(levels, levelId);
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public toggleHeatingAndCooling(roomId: string, { heating, cooling }: { heating?: boolean; cooling?: boolean }): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const updatedLevels = toggleHeatingAndCooling(levels, roomId, { heating, cooling });
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    private patchBuildingToApi(
        updatedLevels: Level[],
        options?: {
            reload?: boolean;
            addInitialCalculation?: boolean;
            innerLoader?: boolean;
            updateCalculations?: boolean;
            calculationBase?: CalculationBase;
        }
    ): void {
        if (!this._activeProjectId) return;

        this._errorSignal.set(false);

        if (options?.innerLoader) {
            this._innerLoadingSignal.set(true);
            this._innerErrorSignal.set(false);
        } else {
            this._loadingSignal.set(true);
        }

        let updatedCalculations = this._calculationSummarySignal();

        if (options?.updateCalculations) {
            updatedCalculations = updateCalculationSummary(this._calculationSummarySignal()?.totalLivingArea ?? 0, updatedLevels);
        } else if (options?.calculationBase) {
            updatedCalculations = updateCalculationSummary(options.calculationBase.totalLivingArea, updatedLevels);
        }

        const buildingPayload: ApiBuilding = {
            levels: mapLevelsToApiModel(updatedLevels),
            rooms: mapRoomsToApiModel(updatedLevels, this._apiRoomsSnapshot, DEFAULT_ROOM_HEIGHT),
            slabs: mapSlabsToApiModel(updatedLevels, this._apiSlabsSnapshot)
        };

        if (options?.calculationBase) {
            buildingPayload.livingArea = options.calculationBase.totalLivingArea;
            const updatedRoomByRoomCalculation = this.updateLeftoverAreaActivation(options.calculationBase.includeTotalBuilding);

            if (updatedRoomByRoomCalculation) {
                buildingPayload.heatLoadCalculations = mapHeatLoadCalculationsToApiModel(
                    [updatedRoomByRoomCalculation],
                    this._apiProjectSnapshot?.buildingType
                );
            }
        } else {
            const heatLoadCalculations = mapHeatLoadCalculationsToApiModel(
                this._apiHeatLoadCalculations,
                this._apiProjectSnapshot?.buildingType
            );

            if (heatLoadCalculations && options?.addInitialCalculation) {
                buildingPayload.heatLoadCalculations = heatLoadCalculations;
            }
        }

        this._roomByRoomApiService
            .patchProject$(this._activeProjectId, {
                building: buildingPayload
            })
            .subscribe({
                next: () => {
                    if (options?.reload) {
                        this._innerLoadingSignal.set(false);
                        this.loadProject(this._activeProjectId!);
                        return;
                    }

                    const project = this._project();
                    if (project) {
                        this._project.set({
                            ...project,
                            building: {
                                ...project.building,
                                levels: buildingPayload.levels,
                                rooms: buildingPayload.rooms,
                                slabs: buildingPayload.slabs
                            }
                        });
                    }
                    this._levelsSignal.set([...updatedLevels]);
                    this._calculationSummarySignal.set(updatedCalculations);
                    // store the latest API snapshots after a successful patch to prevent losing data in subsequent patches
                    this._apiRoomsSnapshot = { ...(buildingPayload.rooms || {}) };
                    this._apiSlabsSnapshot = { ...(buildingPayload.slabs || {}) };
                    if (buildingPayload.heatLoadCalculations) {
                        this._apiHeatLoadCalculations = [...buildingPayload.heatLoadCalculations];
                    }

                    if (options?.calculationBase) {
                        this._calculationBaseSignal.set(options.calculationBase);
                    }

                    this._loadingSignal.set(false);
                    this._innerLoadingSignal.set(false);
                    this._innerErrorSignal.set(false);
                    this._errorSignal.set(false);
                },
                error: _error => {
                    this._loadingSignal.set(false);
                    this._innerLoadingSignal.set(false);
                    if (!options?.innerLoader) {
                        this._errorSignal.set(true);
                        this._levelsSignal.set([]);
                        this._calculationSummarySignal.set(undefined);
                    } else {
                        this._innerErrorSignal.set(true);
                    }
                }
            });
    }

    private extractCalculationBase(building?: ApiBuilding): CalculationBase {
        const roomByRoomCalculation = this.getRoomBasedCalculation(building?.heatLoadCalculations);

        const includeTotalBuilding = Boolean(roomByRoomCalculation?.roomCalculations?.['leftover-area']?.isActive);

        return {
            totalLivingArea: building?.livingArea ?? 0,
            includeTotalBuilding
        };
    }

    private extractDefaultValues(building?: ApiBuilding): DefaultValues {
        return {
            levelHeight: building?.buildingLevelHeight ?? DEFAULT_ROOM_HEIGHT,
            typeOfWindow: building?.standardTypeOfWindow,
            typeOfSunProtection: building?.standardTypeOfSunProtection
        };
    }

    private getRoomBasedCalculation(calculations: ApiHeatLoadCalculation[] | undefined): ApiHeatLoadCalculation | undefined {
        return calculations?.find(calculation => calculation.method === HeatLoadCalculationMethod.RoomByRoom);
    }

    private updateLeftoverAreaActivation(includeTotalBuilding: boolean): ApiHeatLoadCalculation | null {
        const roomByRoomCalculation = this.getRoomBasedCalculation(this._apiHeatLoadCalculations);

        if (!roomByRoomCalculation) {
            return null;
        }

        const leftoverAreaCalculation = roomByRoomCalculation.roomCalculations?.['leftover-area'] ?? {
            roomId: 'leftover-area',
            baseHeatLoad: 0,
            baseCoolingLoad: 0,
            baseHeatDemand: 0,
            isActive: false
        };

        roomByRoomCalculation.roomCalculations = {
            ...(roomByRoomCalculation.roomCalculations ?? {}),
            'leftover-area': {
                ...leftoverAreaCalculation,
                isActive: includeTotalBuilding
            }
        };
        return roomByRoomCalculation;
    }
}
